import { useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector, batch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { clearStoredRid, clearStoredScaActionUrl, getStoredScaActionUrl } from 'logic/sca';
import {
  scaVerifyRedirectParams,
  scaVerificationReset,
  scaPreEnrollReset,
  scaRecipientEnrollReset,
  scaTriggerOff,
} from '../actions';

export const useSCAVerification = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  const verification = useSelector((state) => state.scaVerification);
  const reduxActionUrl = verification.actionUrl;

  // Prefer Redux, fall back to sessionStorage
  const stored = useMemo(() => getStoredScaActionUrl(), [location.key, reduxActionUrl]);
  const actionUrl = reduxActionUrl || stored?.url || null;
  const expired = reduxActionUrl ? false : !!stored?.expired;

  const searchParams = new URLSearchParams(location.search);
  const paramControlStatus = searchParams.get('controlStatus');

  const { result, error, loading, preEnroll, recipientEnroll } = verification;

  // Use values from URL if present, otherwise fallback to Redux state
  const controlStatus = paramControlStatus || result?.controlStatus;

  const hasRedirectParams = Boolean(paramControlStatus);

  const clearSCAFlowState = useCallback(async () => {
    // Remove all SCA-related params from the URL
    const cleanedParams = new URLSearchParams(location.search);
    cleanedParams.delete('controlStatus');

    history.replace({
      pathname: location.pathname,
      search: cleanedParams.toString(),
    });

    // Reset SCA-related Redux state (microtask to preserve current tick, then batch)
    await Promise.resolve();
    batch(() => {
      dispatch(scaPreEnrollReset());
      dispatch(scaRecipientEnrollReset());
      dispatch(scaTriggerOff());
      dispatch(scaVerificationReset());
      clearStoredScaActionUrl();
      clearStoredRid();
    });
  }, [history, location.pathname, location.search, dispatch]);

  // Dispatch verification params if they exist in the URL
  useEffect(() => {
    if (paramControlStatus) {
      dispatch(
        scaVerifyRedirectParams({
          controlStatus: paramControlStatus,
        }),
      );
    }
  }, [paramControlStatus, dispatch]);

  // Determine whether to show dialog
  const showDialog = Boolean(
    !loading && (preEnroll || recipientEnroll || hasRedirectParams || error),
  );

  // Simplified status derivation
  let status = 'idle';

  if (loading) {
    status = 'loading';
  } else if (controlStatus === 'VALIDATED') {
    status = 'success';
  } else if (controlStatus === 'FAILED') {
    status = 'error';
  } else if (error) {
    status = 'error';
  } else if (result) {
    status = 'invalid';
  }

  return {
    verification,
    showDialog,
    clearSCAFlowState,
    status,
    actionUrl,
    expired,
  };
};
